/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import PropTypes from "prop-types";
import React, { createContext, useMemo, useState } from "react";

// Create a new React context for managing header state
export const HeaderContext = createContext();

// Define a provider component for the HeaderContext
export const HeaderContextProvider = ({ children }) => {
  const [ isVisible, setIsVisible ] = useState(true);

  // Create a memoized value object to prevent unnecessary re-renders
  const value = useMemo(() => ({ isVisible, setIsVisible }), [ isVisible, setIsVisible ]);

  return <HeaderContext.Provider value={ value }>{ children }</HeaderContext.Provider>;
};

HeaderContextProvider.propTypes = {
  children: PropTypes.any
};

export default HeaderContext;

// Custom hook to conveniently access the header context values
export const useHeader = () => {
  const context = React.useContext(HeaderContext);

  // Throw an error if the hook is used outside the HeaderContextProvider
  if (context === undefined) {
    throw new Error("useHeader must be used within a HeaderContextProvider");
  }

  // Destructure the context values for easy access
  const { isVisible, setIsVisible } = context;

  return { isVisible, setIsVisible };
};
